/** @format */

// Pure rules of the product catalog (filters, counters, search, sort, export).

export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_DEBOUNCE_MS = 200;

export const DOCUMENT_TYPES = [
  { type: 'data_sheets', label: 'Fiche technique' },
  { type: 'conformity_certificates', label: 'Certificat de conformité' },
  { type: 'warranties', label: 'Garantie' },
  { type: 'user_manuals', label: 'Notice' },
];

export const DOCS_OPTIONS = [
  { key: 'image', label: 'Avec photo' },
  ...DOCUMENT_TYPES.map(({ type, label }) => ({
    key: type,
    label: `Avec ${label.toLowerCase()}`,
  })),
];

const AC = { field: 'alternating_current_type', label: 'Raccordement' };

// Criteria read in the `<typology>_details` block, per typology slug.
export const TYPOLOGY_CRITERIA = {
  'solar-panel': [
    { field: 'nominal_power', label: 'Puissance', unit: 'Wc' },
    { field: 'cell_type', label: 'Type de cellule' },
    { field: 'finish', label: 'Finition' },
  ],
  inverter: [
    {
      field: 'max_ac_power_output',
      label: 'Puissance',
      unit: 'kW',
      scale: 0.001,
    },
    { field: 'is_hybrid', label: 'Hybride' },
    AC,
    { field: 'type', label: 'Type' },
  ],
  optimiser: [{ field: 'nominal_input_power', label: 'Puissance', unit: 'W' }],
  'monitoring-automation': [{ field: 'type', label: 'Type' }, AC],
  structure: [
    { field: 'type', label: "Type d'installation" },
    { field: 'tile_type', label: 'Type de tuile' },
  ],
  cable: [{ field: 'installation_type', label: "Type d'installation" }, AC],
  'electrical-protection': [{ field: 'acdc', label: 'AC/DC' }, AC],
  battery: [
    { field: 'usable_energy', label: 'Capacité utile', unit: 'kWh' },
    { field: 'technology', label: 'Technologie' },
    AC,
  ],
  'heat-pump-aa': [
    { field: 'power', label: 'Puissance', unit: 'kW' },
    { field: 'split_type', label: 'Type de split' },
    { field: 'location_type', label: 'Emplacement' },
  ],
  'sanitary-hot-water-tank': [
    { field: 'capacity', label: 'Capacité', unit: 'L' },
    { field: 'technology', label: 'Technologie' },
    { field: 'mounting_type', label: 'Pose' },
  ],
  'administrative-procedure': [
    AC,
    { field: 'distributor', label: 'Gestionnaire de réseau' },
  ],
  workforce: [
    { field: 'related_typology', label: 'Typologie associée' },
    { field: 'difficulty', label: 'Difficulté' },
  ],
};

const numberFormatter = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
});

export function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function getProductName(product) {
  if (!product) return '';
  const name = product.is_validated
    ? product.public_name || product.custom_public_name
    : product.custom_public_name || product.public_name;
  return name || product.prefixed_internal_name || product.internal_name || '';
}

export function getDetails(product) {
  if (!product) return null;
  const key = Object.keys(product).find(
    (k) =>
      k.endsWith('_details') && product[k] && typeof product[k] === 'object',
  );
  return key ? product[key] : null;
}

export function getManufacturerReference(product) {
  return getDetails(product)?.manufacturer_reference || '';
}

export function getBrandId(product) {
  const { brand } = product || {};
  if (brand == null) return null;
  return typeof brand === 'object' ? brand.id ?? null : brand;
}

export function hasDocument(product, key) {
  if (key === 'image') return Boolean(product?.image || product?.image_url);
  const items = product?.[key];
  return Array.isArray(items) && items.length > 0;
}

// Key used to group a criterion value (null = no value).
export function criterionKey(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'object') {
    const key = raw.slug ?? raw.id ?? raw.name ?? raw.label;
    return key == null ? null : String(key);
  }
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  const n = Number(raw);
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(n)) {
    return String(n);
  }
  return String(raw);
}

export function criterionLabel(criterion, raw, choices) {
  if (raw === null || raw === undefined || raw === '') return '';
  if (typeof raw === 'object') {
    return String(raw.name ?? raw.label ?? raw.slug ?? raw.id ?? '');
  }
  if (typeof raw === 'boolean') return raw ? 'Oui' : 'Non';
  const n = Number(raw);
  if (criterion?.unit && !Number.isNaN(n)) {
    return `${numberFormatter.format(n * (criterion.scale || 1))} ${
      criterion.unit
    }`;
  }
  if (choices?.[raw]) return choices[raw];
  return typeof raw === 'number' || /^-?\d+(\.\d+)?$/.test(String(raw).trim())
    ? numberFormatter.format(n)
    : String(raw);
}

// "6 kW · Hybride · Triphasé": up to 3 criteria values, else the typology name.
export function getKeyLine(product, choicesByField = {}) {
  const details = getDetails(product);
  const criteria = TYPOLOGY_CRITERIA[product?.typology?.slug] || [];
  const parts = [];
  criteria.forEach((criterion) => {
    if (parts.length >= 3 || !details) return;
    const raw = details[criterion.field];
    if (typeof raw === 'boolean') {
      if (raw) parts.push(criterion.label);
      return;
    }
    const label = criterionLabel(
      criterion,
      raw,
      choicesByField[criterion.field],
    );
    if (label && label.toLowerCase() !== 'indifférent') parts.push(label);
  });
  return parts.length ? parts.join(' · ') : product?.typology?.name || '';
}

// Normalized once per product object (products are replaced, not mutated, when they change).
const searchTextCache = new WeakMap();

export function getSearchText(product, brandsById = {}) {
  if (product && searchTextCache.has(product))
    return searchTextCache.get(product);
  const brandId = getBrandId(product);
  const text = normalizeText(
    [
      getProductName(product),
      product?.brand?.name || brandsById[brandId]?.name,
      getManufacturerReference(product),
      product?.typology?.name,
    ]
      .filter(Boolean)
      .join(' '),
  );
  if (product) searchTextCache.set(product, text);
  return text;
}

export function isSearchActive(search) {
  return normalizeText(search).length >= SEARCH_MIN_LENGTH;
}

function searchTokens(search) {
  return isSearchActive(search) ? normalizeText(search).split(' ') : null;
}

function matchesTokens(product, tokens, brandsById) {
  if (!tokens) return true;
  const haystack = getSearchText(product, brandsById);
  return tokens.every((token) => haystack.includes(token));
}

export function matchesSearch(product, search, brandsById) {
  return matchesTokens(product, searchTokens(search), brandsById);
}

export const EMPTY_FILTERS = {
  typology: null,
  subCategory: null,
  brands: [],
  criteria: {},
  docs: [],
  search: '',
  showInactive: false,
};

function buildPredicates(filters, brandsById, searchIds) {
  const tokens = searchTokens(filters.search);
  const criteria = TYPOLOGY_CRITERIA[filters.typology] || [];
  const predicates = {
    typology: (p) => !filters.typology || p.typology?.slug === filters.typology,
    subCategory: (p) =>
      !filters.subCategory ||
      (p.sub_categories || []).some((sc) => sc.slug === filters.subCategory),
    brands: (p) =>
      !filters.brands.length || filters.brands.includes(getBrandId(p)),
    docs: (p) => filters.docs.every((key) => hasDocument(p, key)),
    search: (p) =>
      matchesTokens(p, tokens, brandsById) || Boolean(searchIds?.has(p.id)),
  };
  criteria.forEach(({ field }) => {
    const selected = filters.criteria[field] || [];
    predicates[`criterion:${field}`] = (p) =>
      !selected.length ||
      selected.includes(criterionKey(getDetails(p)?.[field]));
  });
  return predicates;
}

function applyExcept(products, predicates, except) {
  const active = Object.entries(predicates)
    .filter(([name]) => name !== except)
    .map(([, fn]) => fn);
  return products.filter((p) => active.every((fn) => fn(p)));
}

function countBy(products, keysOf) {
  const counts = {};
  products.forEach((p) => {
    keysOf(p).forEach((key) => {
      if (key != null) counts[key] = (counts[key] || 0) + 1;
    });
  });
  return counts;
}

/**
 * Filters the products and computes every counter.
 * A list's counter applies all the other filters but not its own.
 */
// `searchIds`: ids found by the server search (manufacturer references).
export function computeCatalogView(
  products,
  filters,
  brandsById = {},
  searchIds = null,
) {
  const base = (products || []).filter(
    (p) => !p.is_archived && (filters.showInactive || p.is_active),
  );
  const predicates = buildPredicates(filters, brandsById, searchIds);
  const items = applyExcept(base, predicates, null);

  const withoutSubCategory = applyExcept(base, predicates, 'subCategory');
  const criteriaCounts = {};
  (TYPOLOGY_CRITERIA[filters.typology] || []).forEach(({ field }) => {
    criteriaCounts[field] = countBy(
      applyExcept(base, predicates, `criterion:${field}`),
      (p) => [criterionKey(getDetails(p)?.[field])],
    );
  });
  const withoutDocs = applyExcept(base, predicates, 'docs');

  return {
    items,
    typologyCounts: countBy(applyExcept(base, predicates, 'typology'), (p) => [
      p.typology?.slug,
    ]),
    subCategoryCounts: countBy(withoutSubCategory, (p) =>
      (p.sub_categories || []).map((sc) => sc.slug),
    ),
    subCategoryTotal: withoutSubCategory.length,
    brandCounts: countBy(applyExcept(base, predicates, 'brands'), (p) => [
      getBrandId(p),
    ]),
    criteriaCounts,
    docsCounts: countBy(withoutDocs, (p) =>
      DOCS_OPTIONS.filter(({ key }) => hasDocument(p, key)).map(
        ({ key }) => key,
      ),
    ),
  };
}

// A value at zero disappears, unless it is selected.
export function visibleWithCount(values, keyOf, counts, isSelected) {
  return values.filter(
    (value) => (counts[keyOf(value)] || 0) > 0 || isSelected(value),
  );
}

export function sortBrands(brands, counts) {
  return [...brands].sort(
    (a, b) =>
      (counts[b.id] || 0) - (counts[a.id] || 0) ||
      String(a.name).localeCompare(String(b.name), 'fr'),
  );
}

// Changing typology resets the criteria and keeps brands, search and sub-category
// (dropped if the new typology does not have it).
export function selectTypology(filters, typology) {
  const slug = typology?.slug ?? null;
  const keepSubCategory =
    !filters.subCategory ||
    !typology ||
    !Array.isArray(typology.sub_categories) ||
    typology.sub_categories.some(
      (sc) => (sc?.slug ?? sc) === filters.subCategory,
    );
  return {
    ...filters,
    typology: slug,
    criteria: {},
    docs: [],
    subCategory: keepSubCategory ? filters.subCategory : null,
  };
}

// One brand at a time without typology nor sub-category, several otherwise.
export function isSingleBrandMode(filters) {
  return !filters.typology && !filters.subCategory;
}

export function toggleBrand(filters, brandId) {
  const selected = filters.brands.includes(brandId);
  if (isSingleBrandMode(filters)) {
    return { ...filters, brands: selected ? [] : [brandId] };
  }
  return {
    ...filters,
    brands: selected
      ? filters.brands.filter((id) => id !== brandId)
      : [...filters.brands, brandId],
  };
}

export function toggleCriterion(filters, field, key) {
  const current = filters.criteria[field] || [];
  const next = current.includes(key)
    ? current.filter((k) => k !== key)
    : [...current, key];
  return { ...filters, criteria: { ...filters.criteria, [field]: next } };
}

export const SORT_OPTIONS = [
  { value: 'relevance', label: 'Pertinence' },
  { value: 'name', label: 'Nom A → Z' },
  { value: 'brand', label: 'Marque A → Z' },
  { value: 'recent', label: 'Plus récents' },
];

export function sortProducts(products, sort, search, brandsById = {}) {
  const byName = (a, b) =>
    getProductName(a).localeCompare(getProductName(b), 'fr');
  const brandName = (p) =>
    p.brand?.name || brandsById[getBrandId(p)]?.name || '';
  const list = products.map((p, index) => ({ p, index }));
  if (sort === 'name') {
    list.sort((a, b) => byName(a.p, b.p) || a.index - b.index);
  } else if (sort === 'brand') {
    list.sort(
      (a, b) =>
        !brandName(a.p) - !brandName(b.p) ||
        brandName(a.p).localeCompare(brandName(b.p), 'fr') ||
        byName(a.p, b.p),
    );
  } else if (sort === 'recent') {
    // Newest first: ids grow with creation (the light index has no creation date).
    list.sort((a, b) => b.p.id - a.p.id);
  } else if (isSearchActive(search)) {
    const query = normalizeText(search);
    const score = (p) => {
      const name = normalizeText(getProductName(p));
      if (name.startsWith(query)) return 0;
      return name.includes(query) ? 1 : 2;
    };
    list.sort((a, b) => score(a.p) - score(b.p) || a.index - b.index);
  }
  return list.map(({ p }) => p);
}

// Search shortcuts: up to 3 brands, 3 typologies and 3 products.
export function getSuggestions(
  search,
  { products, brands, typologies, brandsById },
) {
  if (!isSearchActive(search)) return [];
  const query = normalizeText(search);
  const tokens = query.split(' ');
  const matchName = (name) => {
    const text = normalizeText(name);
    return tokens.every((t) => text.includes(t));
  };
  return [
    ...brands
      .filter((b) => matchName(b.name))
      .slice(0, 3)
      .map((b) => ({
        kind: 'brand',
        id: `brand-${b.id}`,
        label: b.name,
        value: b,
      })),
    ...typologies
      .filter((t) => matchName(t.name))
      .slice(0, 3)
      .map((t) => ({
        kind: 'typology',
        id: `typology-${t.slug}`,
        label: t.name,
        value: t,
      })),
    ...products
      .filter((p) => matchesSearch(p, search, brandsById))
      .slice(0, 3)
      .map((p) => ({
        kind: 'product',
        id: `product-${p.id}`,
        label: getProductName(p),
        value: p,
      })),
  ];
}

// Where the selling price comes from (mirrors Product.get_price_by_products).
// shortcut: ignores the fallback to coefficient when a formula references missing
// product.details fields; read Product.get_price_formula if that case matters.
export function getPriceSource(product, typology) {
  const productFormula = product?.price_formula || '';
  const typologyFormula = typology?.price_formula || '';
  if (product?.is_kit && Number(product.supplier_price || 0) === 0) {
    return { kind: 'kit', label: 'Somme des prix des éléments du kit' };
  }
  if (productFormula) {
    return {
      kind: 'product',
      label: 'Formule du produit',
      formula: productFormula,
    };
  }
  if (!typologyFormula || typologyFormula.includes('typology.margin')) {
    return {
      kind: 'coefficient',
      label: 'Coefficient de la typologie',
      coefficient: typology?.margin ? Number(typology.margin) : null,
    };
  }
  return {
    kind: 'typology',
    label: 'Formule de la typologie',
    formula: typologyFormula,
  };
}

export function getMargin(price, supplierPrice) {
  const sale = Number(price);
  const purchase = Number(supplierPrice);
  if (!Number.isFinite(sale) || !Number.isFinite(purchase) || sale <= 0) {
    return null;
  }
  const margin = sale - purchase;
  return { margin, markupRate: margin / sale };
}

const BOM = String.fromCharCode(0xfeff);

function csvCell(value) {
  const text = String(value ?? '');
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// CSV for a spreadsheet (no purchase price, formula or margin).
export function buildCatalogCsv(products, { prices = {}, brandsById = {} }) {
  const header = [
    'ID',
    'Typologie',
    'Sous-catégories',
    'Marque',
    'Nom',
    'Référence fabricant',
    'Caractéristiques',
    'Statut',
    'Prix de vente HT (€)',
    'Documents',
  ];
  const rows = products.map((p) => {
    const price = prices[p.id];
    return [
      p.id,
      p.typology?.name,
      (p.sub_categories || []).map((sc) => sc.name).join(', '),
      p.brand?.name || brandsById[getBrandId(p)]?.name,
      getProductName(p),
      getManufacturerReference(p),
      getKeyLine(p),
      p.is_active ? 'Actif' : 'Inactif',
      p.is_active && Number.isFinite(Number(price)) && price !== null
        ? numberFormatter.format(Number(price))
        : '',
      DOCUMENT_TYPES.filter(({ type }) => hasDocument(p, type))
        .map(({ label }) => label)
        .join(', '),
    ];
  });
  return `${BOM}${[header, ...rows]
    .map((row) => row.map(csvCell).join(';'))
    .join('\r\n')}`;
}

// Distinct values of a criterion among the products, sorted (numbers ascending).
export function collectCriterionValues(products, criterion, choices) {
  const values = new Map();
  products.forEach((p) => {
    const raw = getDetails(p)?.[criterion.field];
    const key = criterionKey(raw);
    if (key !== null && !values.has(key)) {
      values.set(key, criterionLabel(criterion, raw, choices));
    }
  });
  const list = [...values].map(([key, label]) => ({ key, label }));
  const numeric = list.every(({ key }) => !Number.isNaN(Number(key)));
  return list.sort((a, b) =>
    numeric
      ? Number(a.key) - Number(b.key)
      : a.label.localeCompare(b.label, 'fr'),
  );
}

// Cuts the index, ordered by brand then id, into one group per brand thanks to the count and
// the first id of each brand. Returns null if the groups do not fit (then one list per brand).
export function cutByBrand(list, heads, noBrandCount) {
  const total = heads.reduce((n, h) => n + h.count, 0) + noBrandCount;
  if (total !== list.length) return null;
  const position = new Map(list.map((p, i) => [p.id, i]));
  const groups = heads
    .filter((h) => h.count > 0)
    .map((h) => ({ ...h, start: position.get(h.firstId) }))
    .sort((a, b) => a.start - b.start);
  const brandOf = {};
  let expected = groups[0]?.start === 0 ? 0 : noBrandCount; // products without brand first or last
  for (let i = 0; i < groups.length; i += 1) {
    const { start, count, brandId } = groups[i];
    if (start !== expected) return null;
    list.slice(start, start + count).forEach((p) => {
      brandOf[p.id] = brandId;
    });
    expected = start + count;
  }
  return brandOf;
}
